import { useMutation } from '@apollo/client';
import { MutationResponse, SendMagicLinkInput } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Modal from '@platform-ui-common/components/Modal/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/actions.js';
import { confirmPasswordField, passwordField } from '@platform-ui-common/hooks/useForm/fields.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/types.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import PasswordValidationCondition from '@platform-ui/auth/components/PasswordValidation/PasswordValidationCondition.js';
import PasswordValidation from '@platform-ui/auth/components/PasswordValidation/index.js';
import SendMagicLink from '@platform-ui/auth/graphql/SendMagicLinkMutation.graphql';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { useEffect, useState } from 'react';

const UpdatePasswordModal = ({ onClose }: { onClose: () => void }) => {
  const {
    identity: { signinMethods, tenantId, auth },
    user,
    resetAuthState,
  } = useAuth();
  const { id: partnerId } = usePartner();

  const [error, setError] = useState('');
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);

  useEffect(() => {
    const checkPasswordExists = async () => {
      if (!user?.gcip?.email || !auth) {
        setHasPassword(false);
        return;
      }

      try {
        // fetchSignInMethodsForEmail queries Firebase backend for actual available sign-in methods
        const signInMethods = await fetchSignInMethodsForEmail(auth, user.gcip.email);
        // 'password' method indicates password is set and can be used for authentication
        setHasPassword(signInMethods.includes('password'));
      } catch {
        setHasPassword(false);
      }
    };

    checkPasswordExists();
  }, [user?.gcip?.email, auth]);

  const {
    dispatch,
    formData,
    errors,
    trySubmit,
    counters: { missingRequiredFields },
  } = useForm(
    {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    {
      currentPassword: passwordField(!!hasPassword, 'currentPassword', 'Current Password'),
      password: passwordField(),
      confirmPassword: confirmPasswordField(),
    },
    ValidationMode.RequiredOnSubmit,
  );

  const [sendLink, { loading: sendLinkLoading }] = useMutation<
    { identity: { sendMagicLink: MutationResponse } },
    { input: SendMagicLinkInput }
  >(SendMagicLink);

  const { showSnackbar } = useSnackbar();

  const updatePassword = async (): Promise<void> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { currentPassword, password } = formData;
    setFirebaseLoading(true);
    signinMethods
      .resetPassword({ currentPassword, password })
      .then((response) => {
        if (response.status === 'success') {
          showSnackbar('Successfully updated password, please sign in with your new password');
          onClose();
          resetAuthState();
          window.location.reload();
        }
        if (response.status === 'error' && response.authMechanism === 'EMAIL_TOKEN') {
          sendLink({
            variables: { input: { partnerId, tenantId, email: user?.gcip.email, timezone } },
          })
            .then(() => {
              showSnackbar(
                <p>
                  For security, we need to verify your identity before setting a new password.
                  Please check <b>{user.gcip.email}</b> for a link to verify your identity and try
                  again.
                </p>,
              );
            })
            .catch(() => setError('Something went wrong'));
        }
      })
      .catch(() => setError('Something went wrong'))
      .finally(() => setFirebaseLoading(false));
  };

  const loading = firebaseLoading || sendLinkLoading;

  return (
    <Modal title="Update password" isOpen onClickClose={onClose} size="S">
      <FormContainer
        onSubmit={() => trySubmit(updatePassword)}
        buttons={
          <div className="flex flex-col gap-6 items-center">
            <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
            <ErrorDisplay message={error} visible={!!error} />
            <Button type="submit" loading={loading} disabled={loading}>
              Update
            </Button>
          </div>
        }
      >
        {hasPassword === true && (
          <TextInput
            id="currentPassword"
            label="Current Password"
            onChange={(value): void => dispatch(fieldUpdate('currentPassword', value))}
            value={formData.currentPassword}
            error={
              Array.isArray(errors.currentPassword) && errors.currentPassword.length > 0
                ? 'Current password does not meet requirements'
                : (errors.currentPassword as string)
            }
            type="password"
            required
          />
        )}
        <TextInput
          id="newPassword"
          label="New Password"
          onChange={(value): void => dispatch(fieldUpdate('password', value))}
          value={formData.password}
          error={
            Array.isArray(errors.password) && errors.password.length > 0
              ? 'Password does not meet requirements'
              : (errors.password as string)
          }
          type="password"
          required
        />
        <PasswordValidation
          password={formData.password}
          errors={(errors.password as string[]) ?? []}
        />
        <TextInput
          id="confirmNewPassword"
          label="Confirm New Password"
          onChange={(value): void => dispatch(fieldUpdate('confirmPassword', value))}
          value={formData.confirmPassword}
          error={errors.confirmPassword as string}
          type="password"
          required
        />
        <PasswordValidationCondition
          label="Passwords match"
          valid={!!formData.confirmPassword && !errors.confirmPassword}
          testId="pvc-match"
        />
      </FormContainer>
    </Modal>
  );
};

export { UpdatePasswordModal };
